import { generatePath } from "react-router-dom";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import type { TagTooltip, TagType } from "@/components/Tag/Tag";
import type { Path } from "@/router";

export const AT_A_GLANCE_TABLE_TEST_ID = "at-a-glance-table";
export const AT_A_GLANCE_TABLE_WRAP_TEST_ID = "at-a-glance-table-wrap";

type AlumniRosterLocation = {
  pathname?: string;
  search?: string;
};

const PGM_UNIVERSITY_LABEL = "PGM University";
const DETAILS_SEPARATOR = " · ";
const DETAIL_LINE_PREFIX =
  /^(Version:|Started:|Completed:|Suspended:|Expired:|Deadline:|Date:|Level Expired:|Level Deadline:)/;

export type AtAGlanceRow = {
  id: string;
  requirement: string;
  status: string;
  statusType: TagType;
  details: string;
  tooltip?: TagTooltip;
  scrollToId?: string;
  onBeforeScroll?: () => void;
  onClick?: () => void;
  link?: MetricCardType<Path>["link"];
};

function statusFromVariant(variant: MetricCardType<Path>["variant"], label: string): string {
  switch (variant) {
    case "success":
      return "Completed";
    case "error":
      return label.includes("Failed") ? "Failed" : "Expired";
    case "warn":
      return label.includes("Suspended:") ? "Suspended" : "In Progress";
    case "info":
      return label.includes("Deadline:") ? "Deadline" : "In Progress";
    default:
      return "Not Started";
  }
}

function tagTypeFromVariant(variant: MetricCardType<Path>["variant"]): TagType {
  switch (variant) {
    case "success":
      return "success";
    case "error":
      return "error";
    case "warn":
      return "warn";
    case "info":
      return "information";
    default:
      return "surface";
  }
}

function toTagTooltip(tooltip: MetricCardType<Path>["tooltip"]): TagTooltip | undefined {
  if (!tooltip) return undefined;

  return {
    title: tooltip.title,
    supportingText: tooltip.supportingText,
    variant: tooltip.variant,
    color: tooltip.color
  };
}

function joinDetailLines(lines: string[]): string {
  return lines.join(DETAILS_SEPARATOR);
}

export function toAtAGlanceRow(metric: MetricCardType<Path>, index: number): AtAGlanceRow {
  const label = metric.label ?? "";
  const shared = {
    tooltip: toTagTooltip(metric.tooltip),
    scrollToId: metric.scrollToId,
    onBeforeScroll: metric.onBeforeScroll,
    onClick: metric.onClick,
    link: metric.link
  };

  if (label === PGM_UNIVERSITY_LABEL) {
    return {
      id: `${index}-${PGM_UNIVERSITY_LABEL}`,
      requirement: PGM_UNIVERSITY_LABEL,
      status: "",
      statusType: "surface",
      details: metric.value ?? "",
      ...shared
    };
  }

  const lines = label.split("\n").filter((line) => line.length > 0);
  const firstIsDetail = lines[0] !== undefined && DETAIL_LINE_PREFIX.test(lines[0]);

  let status: string;
  let details: string;
  if (lines.length === 0) {
    status = statusFromVariant(metric.variant, "");
    details = "";
  } else if (firstIsDetail) {
    status = statusFromVariant(metric.variant, label);
    details = joinDetailLines(lines);
  } else {
    status = lines[0];
    details = joinDetailLines(lines.slice(1));
  }

  return {
    id: `${index}-${metric.value ?? "requirement"}`,
    requirement: metric.value ?? "--",
    status,
    statusType: tagTypeFromVariant(metric.variant),
    details,
    ...shared
  };
}

export function toAtAGlanceRows(metrics: MetricCardType<Path>[]): AtAGlanceRow[] {
  return metrics.map(toAtAGlanceRow);
}

function hrefFromPath(path: string, params: object | undefined): string {
  if (!params) return path;
  return generatePath(path, params as Record<string, string>);
}

export function atAGlanceRowHasDestination(row: AtAGlanceRow): boolean {
  return Boolean(row.link || row.onClick || row.onBeforeScroll || row.scrollToId);
}

export function atAGlanceRowScrollsInPage(row: AtAGlanceRow): boolean {
  return atAGlanceRowHasDestination(row) && !resolveAtAGlanceHref(row);
}

export function resolveAtAGlanceHref(row: AtAGlanceRow): string | null {
  const link = row.link;
  if (!link) return null;

  const to = link.to;
  const params = link.params;

  if (typeof to === "string") {
    return hrefFromPath(to, params);
  }

  const location = to as AlumniRosterLocation;
  if (!location.pathname) return null;

  const pathname = hrefFromPath(location.pathname, params);
  const search = (location.search ?? "").replace(/^\?/, "");
  return search ? `${pathname}?${search}` : pathname;
}
